"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  cooldown = false,
  cooldownMs = 5000,
  onClick,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    cooldown?: boolean;
    cooldownMs?: number;
  }) {
  const Comp = asChild ? Slot : "button";

  const [cooling, setCooling] = React.useState(false);
  const [remainingMs, setRemainingMs] = React.useState(0);
  const endAtRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!cooling) return;
    const id = window.setInterval(() => {
      const left = Math.max(0, (endAtRef.current ?? 0) - Date.now());
      setRemainingMs(left);
      if (left === 0) {
        setCooling(false);
        window.clearInterval(id);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [cooling]);

  const handleCooldownClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (cooling) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    endAtRef.current = Date.now() + cooldownMs;
    setRemainingMs(cooldownMs);
    setCooling(true);
    onClick?.(e);
  };

  const finalOnClick = cooldown ? handleCooldownClick : onClick;
  const remainingSeconds = Math.ceil(remainingMs / 1000);

  return (
    <Comp
      data-slot="button"
      onClick={finalOnClick}
      disabled={cooling || props.disabled}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {cooling ? (
        <span aria-live="polite" aria-atomic="true">
          Wait {remainingSeconds}s
        </span>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
